import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_ENDPOINTS, METHOD_TYPE } from "../../utils/apiUrls";
import api from "../../utils/api";

const initialState = {
    isLoading: false,
    error: null,
    listError: null,
    geographicRiskList: [],
    countriesList: [],
    isUpdating: false,
    isCreating: false,
    isDeleting: false,
    isSyncingFatf: false,
    fatfSyncStatus: null,
    fatfMapCountries: []
}

export const getGeographicRiskList = createAsyncThunk("geographicRisk/getGeographicRiskList", async (queryParams = '', { rejectWithValue }) => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getGeographicRiskList + queryParams
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Unable to load geographic risk data');
    }
});

export const getCountriesList = createAsyncThunk("geographicRisk/getCountriesList", async () => {
    try {
        let data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getCountriesList
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updateGeographicRisk = createAsyncThunk("geographicRisk/updateGeographicRisk", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.put,
            url: API_ENDPOINTS.updateGeographicRisk,
            data: {
                id: payload.id,
                ...payload.data
            }
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const createGeographicRisk = createAsyncThunk("geographicRisk/createGeographicRisk", async (payload) => {
    try {
        let data = {
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.createGeographicRisk,
            data: payload
        };
        const response = await api(data);
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const deleteGeographicRisk = createAsyncThunk("geographicRisk/deleteGeographicRisk", async (id) => {
    try {
        let data = {
            method: METHOD_TYPE.delete,
            url: `${API_ENDPOINTS.deleteGeographicRisk}?id=${id}`
        };
        const response = await api(data);
        return { id, data: response.data };
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getFatfSyncStatus = createAsyncThunk("geographicRisk/getFatfSyncStatus", async () => {
    try {
        const response = await api({ method: METHOD_TYPE.get, url: API_ENDPOINTS.getFatfSyncStatus });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const syncFatfCountries = createAsyncThunk("geographicRisk/syncFatfCountries", async () => {
    try {
        const response = await api({ method: METHOD_TYPE.post, url: API_ENDPOINTS.syncFatfCountries, data: {} });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const getFatfMap = createAsyncThunk("geographicRisk/getFatfMap", async () => {
    try {
        const response = await api({ method: METHOD_TYPE.get, url: API_ENDPOINTS.getFatfMap });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

const geographicRiskSlice = createSlice({
    name: "geographicRisk",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getGeographicRiskList.pending, (state) => {
                state.isLoading = true;
                state.listError = null;
            })
            .addCase(getGeographicRiskList.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.listError = null;
                state.geographicRiskList = Array.isArray(payload?.data) ? payload.data : [];
            })
            .addCase(getGeographicRiskList.rejected, (state, { payload, error }) => {
                state.isLoading = false;
                state.listError = payload || error.message || "Request Failed";
            })
            .addCase(getCountriesList.fulfilled, (state, { payload }) => {
                state.countriesList = payload?.data || [];
            })
            .addCase(updateGeographicRisk.pending, (state) => {
                state.isUpdating = true;
            })
            .addCase(updateGeographicRisk.fulfilled, (state) => {
                state.isUpdating = false;
                state.error = null;
            })
            .addCase(updateGeographicRisk.rejected, (state, { error }) => {
                state.isUpdating = false;
                state.error = error.message || "Update Failed";
            })
            .addCase(createGeographicRisk.pending, (state) => {
                state.isCreating = true;
            })
            .addCase(createGeographicRisk.fulfilled, (state) => {
                state.isCreating = false;
                state.error = null;
            })
            .addCase(createGeographicRisk.rejected, (state, { error }) => {
                state.isCreating = false;
                state.error = error.message || "Create Failed";
            })
            .addCase(deleteGeographicRisk.pending, (state) => {
                state.isDeleting = true;
            })
            .addCase(deleteGeographicRisk.fulfilled, (state) => {
                state.isDeleting = false;
                state.error = null;
            })
            .addCase(deleteGeographicRisk.rejected, (state, { error }) => {
                state.isDeleting = false;
                state.error = error.message || "Delete Failed";
            })
            .addCase(getFatfSyncStatus.fulfilled, (state, { payload }) => {
                state.fatfSyncStatus = payload?.data || null;
            })
            .addCase(getFatfMap.fulfilled, (state, { payload }) => {
                state.fatfMapCountries = payload?.data || [];
            })
            .addCase(syncFatfCountries.pending, (state) => {
                state.isSyncingFatf = true;
            })
            .addCase(syncFatfCountries.fulfilled, (state, { payload }) => {
                state.isSyncingFatf = false;
                state.fatfSyncStatus = { ...(state.fatfSyncStatus || {}), ...(payload?.data || {}), last_sync_at: payload?.data?.synced_at };
            })
            .addCase(syncFatfCountries.rejected, (state, { error }) => {
                state.isSyncingFatf = false;
                state.error = error.message || "FATF refresh failed";
            })
    }
});

const geographicRiskReducer = geographicRiskSlice.reducer;
export { geographicRiskReducer };
export default geographicRiskReducer;
